import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Add,
  Edit,
  ExpandMore,
  ExpandLess,
  Delete
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ROLES } from '../constants/roles';
import AircraftDetails, { getStatusColor } from './AircraftDetails';
import AircraftForm from './AircraftForm';
import AircraftDocumentDialog from './AircraftComponents/AircraftDocumentDialog';
import { utcToDisplayDateAndTime, displayDateTimeToUTC } from '../utils/timezoneUtils';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Aircraft = () => {
  const location = useLocation();
  const [aircraft, setAircraft] = useState([]);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';
  const [open, setOpen] = useState(false);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [aircraftHistory, setAircraftHistory] = useState({});
  const [aircraftDocuments, setAircraftDocuments] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Refresh dropdowns when returning from Master Data Management
  useEffect(() => {
    if (location.state?.refreshDropdowns) {
      fetchDropdownOptions(true); // Force refresh when returning from Master Data
    }
  }, [location.state?.refreshDropdowns]);
  const [formData, setFormData] = useState({
    registration: '',
    manufacturer: '',
    model: '',
    serialNumber: '',
    yearOfManufacture: '',
    dateOfManufacture: '',
    maxPassengers: '',
    basicEmptyWeight: '',
    maxAllUpWeight: '',
    maxRampWeight: '',
    maxLandingWeight: '',
    maxFuelWeight: '',
    fuelConsumptionPerHour: '',
    fuelConsumptionPerHourUnit: 'L',
    fuelConsumptionPerHourWeight: '',
    fuelCapacity: '',
    aircraftType: 'Fixed Wing',
    weightUnit: 'kg',
    status: 'AVAILABLE',
    currentLocation: {
      airport: '',
      hangar: ''
    },
    remarks: '',
    category: '',
    hourType: '',
    owner: '',
    operator: '',
    maintenanceServiceProgramProvider: '',
    warranty: {
      isUnderWarranty: 'No',
      warrantyStartDate: '',
      warrantyEndDate: '',
      techLogPage: ''
    },
    tsn: [],
    otherDetails: {
      aircraftNotInUse: 'No',
      notInUseDate: '',
      isReadOnly: false,
      readOnlyDate: ''
    },
    ataChapterId: '',
    approvedTechnicalLogBookFormatId: '',
    liveTlbMappingUseAircraftOverride: false,
    liveTlbUsageMappingOverride: null
  });
  const [dropdownOptions, setDropdownOptions] = useState({
    CATEGORY: [],
    HOUR_TYPE: [],
    OPERATOR: [],
    ATA_CHAPTERS: [],
    MANUFACTURER_NAMES: [],
    MODELS: [],
    TECHNICAL_LOG_BOOK_FORMATS: []
  });
  const [defaults, setDefaults] = useState({
    CATEGORY: 'Turbo Shaft Rotary Wing Aircraft',
    HOUR_TYPE: 'Hours',
    OPERATOR: ''
  });
  const [documentForm, setDocumentForm] = useState({
    documentType: '',
    documentSubType: '',
    description: '',
    file: null
  });
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [selectedAircraftForDocument, setSelectedAircraftForDocument] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [formTab, setFormTab] = useState(0);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [lastDropdownRefresh, setLastDropdownRefresh] = useState(null);
  const [liveUsageByAircraftId, setLiveUsageByAircraftId] = useState({});
  const [liveUsageLoadingByAircraftId, setLiveUsageLoadingByAircraftId] = useState({});
  const [liveUsageErrorByAircraftId, setLiveUsageErrorByAircraftId] = useState({});
  const { user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    fetchAircraft();
    fetchDropdownOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for master data updates to refresh dropdowns
  useEffect(() => {
    const handleMasterDataUpdate = (event) => {
      // Refresh dropdown options when master data is updated
      fetchDropdownOptions(true); // Force refresh
    };

    window.addEventListener('masterDataUpdated', handleMasterDataUpdate);

    return () => {
      window.removeEventListener('masterDataUpdated', handleMasterDataUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDropdownOptions = async (forceRefresh = false) => {
    // Prevent multiple simultaneous calls
    if (dropdownsLoading) {
      console.log('Dropdown options already loading, skipping...');
      return;
    }
    
    // Prevent refreshing too frequently (throttle to once per 5 seconds)
    const now = Date.now();
    if (!forceRefresh && lastDropdownRefresh && (now - lastDropdownRefresh) < 5000) {
      console.log('Dropdown options refreshed recently, skipping...');
      return;
    }
    
    setDropdownsLoading(true);
    try {
      // Use Promise.all to fetch in parallel instead of sequentially
      const [optionsResponse, ataResponse, manufacturerNamesResponse, modelsResponse, formatHistoryResponse, defaultFormatResponse] = await Promise.allSettled([
        axios.get('/api/aircraft/options'),
        axios.get('/api/master-data/ata-chapters', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/manufacturer-names', { params: { isActive: 'true' } }),
        axios.get('/api/master-data/models', { params: { isActive: 'true' } }),
        axios.get('/api/technical-log-book-format/history').catch(() => ({ data: [] })),
        axios.get('/api/technical-log-book-format/').catch(() => ({ data: null }))
      ]);
      
      // Handle options response
      if (optionsResponse.status === 'fulfilled') {
        setDropdownOptions(prev => ({
          ...prev,
          ...optionsResponse.value.data.options
        }));
        setDefaults(optionsResponse.value.data.defaults);
        
        if (!selectedAircraft) {
          setFormData(prev => ({
            ...prev,
            category: optionsResponse.value.data.defaults.CATEGORY || '',
            hourType: optionsResponse.value.data.defaults.HOUR_TYPE || ''
          }));
        }
      } else {
        console.error('Error fetching aircraft options:', optionsResponse.reason);
      }
      
      // Handle ATA Chapters
      if (ataResponse.status === 'fulfilled') {
        setDropdownOptions(prev => ({
          ...prev,
          ATA_CHAPTERS: ataResponse.value.data
        }));
      } else {
        console.error('Error fetching ATA chapters:', ataResponse.reason);
      }
      
      // Handle Manufacturer Names
      if (manufacturerNamesResponse.status === 'fulfilled') {
        setDropdownOptions(prev => ({
          ...prev,
          MANUFACTURER_NAMES: manufacturerNamesResponse.value.data || []
        }));
      } else {
        console.error('Error fetching manufacturer names:', manufacturerNamesResponse.reason);
      }
      
      // Handle Models
      if (modelsResponse.status === 'fulfilled') {
        setDropdownOptions(prev => ({
          ...prev,
          MODELS: modelsResponse.value.data || []
        }));
      } else {
        console.error('Error fetching models:', modelsResponse.reason);
      }

      // Handle Technical Log Book Formats
      if (formatHistoryResponse.status === 'fulfilled' && Array.isArray(formatHistoryResponse.value.data)) {
        setDropdownOptions(prev => ({
          ...prev,
          TECHNICAL_LOG_BOOK_FORMATS: formatHistoryResponse.value.data
        }));
        if (!selectedAircraft && defaultFormatResponse.status === 'fulfilled' && defaultFormatResponse.value.data?._id) {
          setFormData(prev => ({
            ...prev,
            approvedTechnicalLogBookFormatId: defaultFormatResponse.value.data._id
          }));
        }
      }
      
      setLastDropdownRefresh(now);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
      // Don't show error to user if it's a 429 - the interceptor will handle it
      if (error.response?.status !== 429) {
        console.error('Unexpected error fetching dropdowns:', error);
      }
    } finally {
      setDropdownsLoading(false);
    }
  };

  const createOperatorOption = async (operatorValue) => {
    try {
      const response = await axios.post('/api/aircraft-options', {
        optionType: 'OPERATOR',
        value: operatorValue.trim(),
        isDefault: false,
        displayOrder: (dropdownOptions.OPERATOR || []).length
      });
      
      // Refresh dropdown options to include the new operator
      await fetchDropdownOptions();
      
      return response.data;
    } catch (error) {
      console.error('Error creating operator option:', error);
      if (error.response?.status === 400 && error.response?.data?.message?.includes('already exists')) {
        // Option already exists, just refresh the options
        await fetchDropdownOptions();
        return;
      }
      throw error;
    }
  };


  const fetchAircraft = async () => {
    try {
      const response = await axios.get('/api/aircraft');
      setAircraft(response.data);
    } catch (error) {
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchAircraftHistory = async (aircraftId) => {
    try {
      const response = await axios.get(`/api/aircraft/${aircraftId}/history`);
      setAircraftHistory(prev => ({ ...prev, [aircraftId]: response.data }));
    } catch (error) {
      console.error('Error fetching aircraft history:', error);
    }
  };

  const fetchAircraftDocuments = async (aircraftId) => {
    try {
      const response = await axios.get(`/api/aircraft/${aircraftId}/documents`);
      setAircraftDocuments(prev => ({ ...prev, [aircraftId]: response.data }));
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchLiveUsageForAircraft = async (aircraftId) => {
    if (!aircraftId) return;
    setLiveUsageLoadingByAircraftId(prev => ({ ...prev, [aircraftId]: true }));
    setLiveUsageErrorByAircraftId(prev => ({ ...prev, [aircraftId]: null }));
    try {
      const response = await axios.get(`/api/aircraft/${aircraftId}/live-usage`);
      setLiveUsageByAircraftId(prev => ({ ...prev, [aircraftId]: response.data || null }));
    } catch (error) {
      console.error('Error fetching aircraft live usage:', error);
      setLiveUsageByAircraftId(prev => ({ ...prev, [aircraftId]: null }));
      setLiveUsageErrorByAircraftId(prev => ({
        ...prev,
        [aircraftId]: error?.response?.data?.message || 'Unable to fetch live usage'
      }));
    } finally {
      setLiveUsageLoadingByAircraftId(prev => ({ ...prev, [aircraftId]: false }));
    }
  };

  const handleRowExpand = async (aircraftId) => {
    const isExpanded = expandedRows[aircraftId];
    setExpandedRows(prev => ({ ...prev, [aircraftId]: !isExpanded }));
    
    if (!isExpanded) {
      setDetailTab(0);
      await Promise.all([
        fetchAircraftHistory(aircraftId),
        fetchAircraftDocuments(aircraftId)
      ]);
    }
  };

  const handleSubmit = async () => {
    try {
      const hasValue = (val) => val !== '' && val !== null && val !== undefined;
      const toNumber = (val) => val !== undefined && val !== null && val !== '' ? Number(val) : undefined;
      const toDate = (val) => {
        if (!val) return undefined;
        if (displayTimezone) {
          const iso = displayDateTimeToUTC(String(val).slice(0, 10), '00:00', displayTimezone);
          return iso ? new Date(iso) : new Date(val);
        }
        return new Date(val);
      };
      
      const submitData = {
        registration: formData.registration,
        manufacturer: formData.manufacturer,
        model: formData.model,
        serialNumber: formData.serialNumber,
        maxPassengers: Number(formData.maxPassengers),
        basicEmptyWeight: Number(formData.basicEmptyWeight),
        basicEmptyWeightUnit: formData.weightUnit,
        aircraftType: formData.aircraftType,
        status: formData.status,
        remarks: formData.remarks,
        maxAllUpWeightUnit: hasValue(formData.maxAllUpWeight) ? formData.weightUnit : undefined,
        maxRampWeightUnit: hasValue(formData.maxRampWeight) ? formData.weightUnit : undefined,
        maxLandingWeightUnit: hasValue(formData.maxLandingWeight) ? formData.weightUnit : undefined,
        maxFuelWeightUnit: hasValue(formData.maxFuelWeight) ? formData.weightUnit : undefined,
        maxAllUpWeight: toNumber(formData.maxAllUpWeight),
        maxRampWeight: toNumber(formData.maxRampWeight),
        maxLandingWeight: toNumber(formData.maxLandingWeight),
        maxFuelWeight: toNumber(formData.maxFuelWeight),
        fuelConsumptionPerHour: toNumber(formData.fuelConsumptionPerHour),
        fuelConsumptionPerHourUnit: hasValue(formData.fuelConsumptionPerHour) ? formData.fuelConsumptionPerHourUnit : undefined,
        fuelConsumptionPerHourWeight: toNumber(formData.fuelConsumptionPerHourWeight),
        fuelCapacity: toNumber(formData.fuelCapacity),
        yearOfManufacture: toNumber(formData.yearOfManufacture),
        dateOfManufacture: toDate(formData.dateOfManufacture),
        currentLocation: formData.currentLocation.airport || formData.currentLocation.hangar
          ? {
              airport: formData.currentLocation.airport,
              hangar: formData.currentLocation.hangar,
              updatedAt: new Date()
            }
          : undefined,
        category: formData.category || undefined,
        hourType: formData.hourType || undefined,
        owner: formData.owner || undefined,
        operator: formData.operator || undefined,
        maintenanceServiceProgramProvider: formData.maintenanceServiceProgramProvider || undefined,
        ataChapterId: formData.ataChapterId || undefined,
        warranty: formData.warranty.isUnderWarranty === 'Yes' ? {
          isUnderWarranty: formData.warranty.isUnderWarranty,
          warrantyStartDate: toDate(formData.warranty.warrantyStartDate),
          warrantyEndDate: toDate(formData.warranty.warrantyEndDate),
          techLogPage: formData.warranty.techLogPage || undefined
        } : undefined,
        tsn: formData.tsn.map(tsnItem => {
          const keyValues = tsnItem.keyValues || [];
          
          // Known fields that are stored separately (not in additionalFields)
          const knownFields = ['Hours', 'Manufacturing Date', 'Landings'];
          
          // Build additionalFields with only custom fields (excluding known fields and empty values)
          const additionalFields = {};
          keyValues.forEach(kv => {
            // Only include fields that:
            // 1. Have a period name
            // 2. Are not in the known fields list
            // 3. Have a non-empty value
            if (kv.period && 
                !knownFields.includes(kv.period) && 
                kv.value !== null && 
                kv.value !== undefined && 
                String(kv.value).trim() !== '') {
              additionalFields[kv.period] = kv.value;
            }
          });

          // Derive known fields for backward compatibility
          const hoursKV = keyValues.find(kv => kv.period === 'Hours' && kv.value && String(kv.value).trim() !== '');
          const mfgKV = keyValues.find(kv => kv.period === 'Manufacturing Date' && kv.value && String(kv.value).trim() !== '');
          const landingsKV = keyValues.find(kv => kv.period === 'Landings' && kv.value !== null && kv.value !== undefined && String(kv.value).trim() !== '');

          return {
            asOnDate: toDate(tsnItem.asOnDate),
            period: hoursKV ? 'Hours' : undefined,
            periodValue: hoursKV ? (hoursKV.value || undefined) : undefined,
            manufacturingDate: mfgKV ? toDate(mfgKV.value) : undefined,
            landings: landingsKV ? toNumber(landingsKV.value) : undefined,
            additionalFields: Object.keys(additionalFields).length > 0 ? additionalFields : undefined
          };
        }).filter(tsnItem => tsnItem.asOnDate), // Only include TSN entries with valid dates
        otherDetails: {
          aircraftNotInUse: formData.otherDetails.aircraftNotInUse,
          notInUseDate: toDate(formData.otherDetails.notInUseDate),
          isReadOnly: formData.otherDetails.isReadOnly,
          readOnlyDate: toDate(formData.otherDetails.readOnlyDate)
        },
        approvedTechnicalLogBookFormatId: formData.approvedTechnicalLogBookFormatId || undefined,
        liveTlbUsageMappingOverride: formData.liveTlbMappingUseAircraftOverride
          ? formData.liveTlbUsageMappingOverride || {}
          : null
      };

      if (selectedAircraft) {
        await axios.put(`/api/aircraft/${selectedAircraft._id}`, submitData);
        showToast(`Aircraft ${submitData.registration} updated successfully`, 'success');
      } else {
        await axios.post('/api/aircraft', submitData);
        showToast(`Aircraft ${submitData.registration} created successfully`, 'success');
      }
      fetchAircraft();
      handleClose();
    } catch (error) {
      console.error('Error saving aircraft:', error);
      const errorMessage = error.response?.data?.message || 
                          (error.response?.data?.errors && Array.isArray(error.response.data.errors) 
                            ? error.response.data.errors.map(e => e.msg || e.message).join(', ')
                            : 'Error saving aircraft');
      showToast(errorMessage, 'error');
    }
  };

  const handleDelete = async (aircraftId) => {
    if (!window.confirm('Are you sure you want to delete this aircraft? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/aircraft/${aircraftId}`);
      fetchAircraft();
      alert('Aircraft deleted successfully');
    } catch (error) {
      console.error('Error deleting aircraft:', error);
      alert(error.response?.data?.message || 'Error deleting aircraft');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedAircraft(null);
    setFormTab(0);
    setFormData({
      registration: '',
      manufacturer: '',
      model: '',
      serialNumber: '',
      yearOfManufacture: '',
      dateOfManufacture: '',
      maxPassengers: '',
      basicEmptyWeight: '',
      maxAllUpWeight: '',
      maxRampWeight: '',
      maxLandingWeight: '',
      maxFuelWeight: '',
      fuelConsumptionPerHour: '',
      fuelConsumptionPerHourUnit: 'L',
      fuelConsumptionPerHourWeight: '',
      fuelCapacity: '',
      aircraftType: 'Fixed Wing',
      weightUnit: 'kg',
      status: 'AVAILABLE',
      currentLocation: {
        airport: '',
        hangar: ''
      },
      remarks: '',
      category: defaults.CATEGORY || '',
      hourType: defaults.HOUR_TYPE || '',
      owner: '',
      operator: '',
      maintenanceServiceProgramProvider: '',
      warranty: {
        isUnderWarranty: 'No',
        warrantyStartDate: '',
        warrantyEndDate: '',
        techLogPage: ''
      },
      tsn: [],
      otherDetails: {
        aircraftNotInUse: 'No',
        notInUseDate: '',
        isReadOnly: false,
        readOnlyDate: ''
      },
      ataChapterId: '',
      approvedTechnicalLogBookFormatId: '',
      liveTlbMappingUseAircraftOverride: false,
      liveTlbUsageMappingOverride: null
    });
  };

  const handleEdit = (aircraftItem) => {
    setSelectedAircraft(aircraftItem);
    fetchLiveUsageForAircraft(aircraftItem?._id);
    const weightUnit = aircraftItem.basicEmptyWeightUnit || 
                       aircraftItem.maxAllUpWeightUnit || 
                       aircraftItem.maxRampWeightUnit || 
                       aircraftItem.maxLandingWeightUnit || 
                       aircraftItem.maxFuelWeightUnit || 
                       'kg';
    
    const getValue = (val, defaultValue = '') => (val !== null && val !== undefined ? val : defaultValue);
    const getDateValue = (val) => {
      if (!val) return '';
      if (displayTimezone) {
        const d = utcToDisplayDateAndTime(val, displayTimezone).date;
        return d || new Date(val).toISOString().split('T')[0];
      }
      return new Date(val).toISOString().split('T')[0];
    };

    setFormData({
      registration: getValue(aircraftItem.registration),
      manufacturer: getValue(aircraftItem.manufacturer),
      model: getValue(aircraftItem.model),
      serialNumber: getValue(aircraftItem.serialNumber),
      yearOfManufacture: getValue(aircraftItem.yearOfManufacture),
      dateOfManufacture: getDateValue(aircraftItem.dateOfManufacture),
      maxPassengers: getValue(aircraftItem.maxPassengers),
      basicEmptyWeight: getValue(aircraftItem.basicEmptyWeight),
      maxAllUpWeight: getValue(aircraftItem.maxAllUpWeight),
      maxRampWeight: getValue(aircraftItem.maxRampWeight),
      maxLandingWeight: getValue(aircraftItem.maxLandingWeight),
      maxFuelWeight: getValue(aircraftItem.maxFuelWeight),
      fuelConsumptionPerHour: getValue(aircraftItem.fuelConsumptionPerHour),
      fuelConsumptionPerHourUnit: getValue(aircraftItem.fuelConsumptionPerHourUnit, 'L'),
      fuelConsumptionPerHourWeight: getValue(aircraftItem.fuelConsumptionPerHourWeight),
      fuelCapacity: getValue(aircraftItem.fuelCapacity),
      aircraftType: getValue(aircraftItem.aircraftType, 'Fixed Wing'),
      weightUnit: weightUnit,
      status: getValue(aircraftItem.status, 'AVAILABLE'),
      currentLocation: aircraftItem.currentLocation || { airport: '', hangar: '' },
      remarks: getValue(aircraftItem.remarks),
      category: getValue(aircraftItem.category, defaults.CATEGORY || ''),
      hourType: getValue(aircraftItem.hourType, defaults.HOUR_TYPE || ''),
      owner: getValue(aircraftItem.owner),
      operator: getValue(aircraftItem.operator),
      maintenanceServiceProgramProvider: getValue(aircraftItem.maintenanceServiceProgramProvider),
      warranty: {
        isUnderWarranty: getValue(aircraftItem.warranty?.isUnderWarranty, 'No'),
        warrantyStartDate: getDateValue(aircraftItem.warranty?.warrantyStartDate),
        warrantyEndDate: getDateValue(aircraftItem.warranty?.warrantyEndDate),
        techLogPage: getValue(aircraftItem.warranty?.techLogPage)
      },
      tsn: (aircraftItem.tsn || []).map(entry => {
        const keyValues = [];

        if (entry.period || entry.periodValue) {
          keyValues.push({
            period: entry.period || 'Hours',
            value: entry.periodValue || ''
          });
        }

        if (entry.manufacturingDate) {
          keyValues.push({
            period: 'Manufacturing Date',
            value: getDateValue(entry.manufacturingDate)
          });
        }

        if (entry.landings !== undefined && entry.landings !== null) {
          keyValues.push({
            period: 'Landings',
            value: String(entry.landings)
          });
        }

        if (entry.additionalFields && typeof entry.additionalFields === 'object') {
          Object.entries(entry.additionalFields).forEach(([period, value]) => {
            // Only include non-empty values to avoid showing removed/empty fields
            if (value != null && String(value).trim() !== '') {
              keyValues.push({
                period,
                value: String(value)
              });
            }
          });
        }

        // Filter out empty keyValues (period without value or empty value)
        const filteredKeyValues = keyValues.filter(kv => 
          kv.period && kv.value != null && String(kv.value).trim() !== ''
        );

        // Ensure at least one editable row if we have a valid TSN entry
        if (filteredKeyValues.length === 0 && entry.asOnDate) {
          filteredKeyValues.push({ period: 'Hours', value: '' });
        }

        return {
          asOnDate: getDateValue(entry.asOnDate),
          keyValues: filteredKeyValues,
          additionalFields: entry.additionalFields || {}
        };

      }).filter(tsnEntry => tsnEntry.asOnDate), // Only include entries with valid dates
      otherDetails: {
        aircraftNotInUse: getValue(aircraftItem.otherDetails?.aircraftNotInUse, 'No'),
        notInUseDate: getDateValue(aircraftItem.otherDetails?.notInUseDate),
        isReadOnly: aircraftItem.otherDetails?.isReadOnly || false,
        readOnlyDate: getDateValue(aircraftItem.otherDetails?.readOnlyDate)
      },
      ataChapterId: aircraftItem.ataChapterId ? String(aircraftItem.ataChapterId) : '',
      approvedTechnicalLogBookFormatId: aircraftItem.approvedTechnicalLogBookFormatId
        ? (typeof aircraftItem.approvedTechnicalLogBookFormatId === 'object' && aircraftItem.approvedTechnicalLogBookFormatId?._id
          ? String(aircraftItem.approvedTechnicalLogBookFormatId._id)
          : String(aircraftItem.approvedTechnicalLogBookFormatId))
        : '',
      liveTlbMappingUseAircraftOverride: !!aircraftItem.liveTlbUsageMappingOverride,
      liveTlbUsageMappingOverride: aircraftItem.liveTlbUsageMappingOverride || null
    });
    setOpen(true);
  };

  const handleDocumentUpload = async () => {
    if (!documentForm.file || !documentForm.documentType) {
      alert('Please select a file and document type');
      return;
    }
    if (!selectedAircraftForDocument) {
      alert('No aircraft selected');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('file', documentForm.file);
      formDataToSend.append('documentType', documentForm.documentType);
      if (documentForm.documentSubType) {
        formDataToSend.append('documentSubType', documentForm.documentSubType);
      }
      if (documentForm.description) {
        formDataToSend.append('description', documentForm.description);
      }

      await axios.post(
        `/api/aircraft/${selectedAircraftForDocument._id}/documents`,
        formDataToSend,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      await fetchAircraftDocuments(selectedAircraftForDocument._id);
      setDocumentDialogOpen(false);
      setDocumentForm({
        documentType: '',
        documentSubType: '',
        description: '',
        file: null
      });
      alert('Document uploaded successfully');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert(error.response?.data?.message || 'Error uploading document');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDocumentDownload = async (aircraftId, docId, fileName) => {
    try {
      const response = await axios.get(
        `/api/aircraft/${aircraftId}/documents/${docId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Error downloading document');
    }
  };

  const openDocumentDialog = (aircraftItem) => {
    setSelectedAircraftForDocument(aircraftItem);
    setDocumentDialogOpen(true);
  };

  const canManageAircraft = [ROLES.SUPER_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER].includes(user?.role);
  const canUploadDocuments = [ROLES.SUPER_ADMIN, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER].includes(user?.role);
  const canViewDetails = [
    ROLES.SUPER_ADMIN,
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.CA_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.MRO_MANAGER,
    ROLES.MRO_ENGINEER,
    ROLES.MRO_QM_MANAGER,
    ROLES.MRO_QM_OFFICER
  ].includes(user?.role);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Aircraft Fleet</Typography>
        {canManageAircraft && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
          >
            Add Aircraft
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px"></TableCell>
              <TableCell>Registration</TableCell>
              <TableCell>Aircraft</TableCell>
              <TableCell>Serial Number</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Flight Hours</TableCell>
              <TableCell>Next Inspection</TableCell>
              <TableCell>Current Location</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aircraft.map((ac) => {
              const isExpanded = expandedRows[ac._id];
              const history = aircraftHistory[ac._id];
              const documents = aircraftDocuments[ac._id] || [];

              return (
                <React.Fragment key={ac._id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowExpand(ac._id)}
                      >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell><strong>{ac.registration}</strong></TableCell>
                    <TableCell>{ac.manufacturer} {ac.model}</TableCell>
                    <TableCell>{ac.serialNumber}</TableCell>
                    <TableCell>
                      {ac.maxPassengers} pax / {ac.basicEmptyWeight} {ac.basicEmptyWeightUnit || 'kg'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ac.status}
                        color={getStatusColor(ac.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{ac.flightHours || 0} hrs</TableCell>
                    <TableCell>
                      {ac.nextInspection ? new Date(ac.nextInspection).toLocaleDateString() : 'Not scheduled'}
                    </TableCell>
                    <TableCell>
                      {ac.currentLocation?.airport || '—'}
                      {ac.currentLocation?.hangar && ` / ${ac.currentLocation.hangar}`}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEdit(ac)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      {user?.role === ROLES.SUPER_ADMIN && (
                        <Button
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => handleDelete(ac._id)}
                          color="error"
                          variant="outlined"
                        >
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} style={{ padding: 0, border: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <AircraftDetails
                          aircraft={ac}
                          history={history}
                          documents={documents}
                          detailTab={detailTab}
                          setDetailTab={setDetailTab}
                          onDocumentDownload={handleDocumentDownload}
                          onDocumentUpload={openDocumentDialog}
                          canUploadDocuments={canUploadDocuments}
                          canViewDetails={canViewDetails}
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <AircraftForm
        open={open}
        onClose={handleClose}
        selectedAircraft={selectedAircraft}
        formData={formData}
        setFormData={setFormData}
        formTab={formTab}
        setFormTab={setFormTab}
        dropdownOptions={dropdownOptions}
        displayTimezone={displayTimezone}
        onCreateOperator={createOperatorOption}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        user={user}
        onRefreshDropdowns={(forceRefresh = false) => fetchDropdownOptions(forceRefresh)}
        liveUsage={selectedAircraft?._id ? liveUsageByAircraftId[selectedAircraft._id] : null}
        liveUsageLoading={selectedAircraft?._id ? !!liveUsageLoadingByAircraftId[selectedAircraft._id] : false}
        liveUsageError={selectedAircraft?._id ? liveUsageErrorByAircraftId[selectedAircraft._id] : null}
        onOpen={() => {
          // Only refresh if dropdowns haven't been refreshed recently
          // This prevents too many API calls when opening/closing the form
          // Don't force refresh on open - use throttling
          fetchDropdownOptions(false);
        }}
      />

      <AircraftDocumentDialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        documentForm={documentForm}
        setDocumentForm={setDocumentForm}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onUpload={handleDocumentUpload}
      />
    </Box>
  );
};

export default Aircraft;
